import { useMemo } from "react";
import {
  normalizeScheduleCellCode,
  resolveShiftFromScheduleCell,
  resolveShiftFromScheduleCode,
} from "@/utils/shift-util";

type ShiftDef = {
  shift_id: string;
  default_shift?: string | null;
  sequence?: number;
  id?: number;
};

export type ScheduleStatsKeyMode = "shift_id" | "shift_db_id";

/**
 * @param shiftDefinitions - 있으면 각 칸을 resolve하여 집계
 * @param scheduleIds - 있으면 `resolveShiftFromScheduleCell` (편집·API id 정합)
 * @param keyMode - `shift_db_id`: 동일 shift_id 문자가 둘 이상일 때 구분(권장). `shift_id`: 기존 문자열 키
 */
export function useScheduleStats(
  schedule: (string | null | { code: string | null } | null)[],
  shiftDefinitions?: ShiftDef[] | null,
  scheduleIds?: (number | null)[] | null,
  keyMode: ScheduleStatsKeyMode = "shift_id"
) {
  return useMemo(() => {
    if (!schedule || !Array.isArray(schedule)) {
      return {} as Record<string, number>;
    }

    const defs = shiftDefinitions?.length ? shiftDefinitions : null;
    const useDbId = keyMode === "shift_db_id";

    return schedule.reduce<Record<string, number>>((acc, shift, index) => {
      if (shift === null || shift === undefined) {
        return acc;
      }

      const trimmedShift = normalizeScheduleCellCode(shift).trim();

      if (!trimmedShift || trimmedShift === "-") {
        return acc;
      }

      let key: string;
      if (defs != null) {
        const resolved =
          scheduleIds != null && scheduleIds.length > 0
            ? resolveShiftFromScheduleCell(
                normalizeScheduleCellCode(shift) || null,
                scheduleIds[index] ?? null,
                defs
              )
            : resolveShiftFromScheduleCode(trimmedShift, defs);
        if (
          useDbId &&
          resolved?.id != null &&
          Number.isFinite(Number(resolved.id))
        ) {
          key = `id:${resolved.id}`;
        } else {
          key = resolved?.shift_id ?? trimmedShift;
        }
      } else {
        key = trimmedShift;
      }

      acc[key] = (acc[key] ?? 0) + 1;
      return acc;
    }, {});
  }, [schedule, shiftDefinitions, scheduleIds, keyMode]);
}
